import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import UpgradeModal from "../components/UpgradeModal";
import { Search, Filter, CheckCircle2, PlayCircle, Lock, LayoutGrid, Layers } from "lucide-react";

const DIFFS = ["all", "easy", "medium", "hard"];
const LAYOUT_KEY = "hypexlabs.labs.layout";   // "domain" (original) | "stage" (SwitchLab-style)
const STATUSES = [
  { id: "all", label: "all" },
  { id: "not_started", label: "unstarted" },
  { id: "in_progress", label: "in progress" },
  { id: "completed", label: "completed" },
];

function DifficultyBadge({ d }) {
  const colors = {
    easy: "border-[#00FF41]/40 text-[#00FF41]",
    medium: "border-[#00E5FF]/40 text-[#00E5FF]",
    hard: "border-[#F59E0B]/40 text-[#F59E0B]",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest font-mono border ${colors[d] || ""}`}
    >
      {d}
    </span>
  );
}

function StatusPill({ s, score }) {
  if (s === "completed") {
    return (
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono text-[#00FF41]">
        <CheckCircle2 size={12} /> done · {score}
      </span>
    );
  }
  if (s === "in_progress") {
    return (
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono text-[#00E5FF]">
        <PlayCircle size={12} /> in progress
      </span>
    );
  }
  return (
    <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-600">unstarted</span>
  );
}

export default function Labs() {
  const [data, setData] = useState({ labs: [], domains: [] });
  const [tier, setTier] = useState("free");
  const [billing, setBilling] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("all");
  const [diff, setDiff] = useState("all");
  const [status, setStatus] = useState("all");
  const [layout, setLayout] = useState(() => {
    try { return localStorage.getItem(LAYOUT_KEY) || "domain"; } catch { return "domain"; }
  });
  const toggleLayout = () => {
    const next = layout === "domain" ? "stage" : "domain";
    setLayout(next);
    try { localStorage.setItem(LAYOUT_KEY, next); } catch { /* */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: labsData }, { data: billingStatus }] = await Promise.all([
          api.get("/labs"),
          api.get("/billing/status").catch(() => ({ data: { tier: "free" } })),
        ]);
        setData(labsData);
        setTier(billingStatus?.tier || "free");
        setBilling(billingStatus);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  const freeQuotaExhausted =
    billing &&
    tier === "free" &&
    (billing.labs_used ?? 0) >= (billing.labs_limit ?? 3);

  const filtered = useMemo(() => {
    return (data.labs || []).filter((l) => {
      if (domain !== "all" && l.domain_code !== domain) return false;
      if (diff !== "all" && l.difficulty !== diff) return false;
      if (status !== "all" && l.status !== status) return false;
      if (q && !(l.title.toLowerCase().includes(q.toLowerCase()) || l.topic.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
  }, [data, domain, diff, status, q]);

  const byDomain = useMemo(() => {
    const g = {};
    filtered.forEach((l) => {
      const key = l.domain;
      g[key] = g[key] || { code: l.domain_code, labs: [] };
      g[key].labs.push(l);
    });
    return g;
  }, [filtered]);

  // Stage view — read the already-computed stages tree from the API and
  // filter its labs against the user's current search/domain/diff/status.
  const stagesFiltered = useMemo(() => {
    if (!data.stages) return [];
    const allowed = new Set(filtered.map((l) => l.id));
    return data.stages
      .map((st) => ({
        ...st,
        sections: st.sections
          .map((sec) => ({
            ...sec,
            labs: sec.labs.filter((l) => allowed.has(l.id)),
          }))
          .filter((sec) => sec.labs.length > 0),
      }))
      .filter((st) => st.sections.length > 0);
  }, [data.stages, filtered]);

  // Render a single lab card. Extracted so both layouts reuse it.
  const renderLabCard = (l) => {
    const proLocked = l.is_pro_only && tier !== "pro";
    const quotaLocked =
      freeQuotaExhausted && l.status === "not_started" && !l.is_pro_only;
    const locked = proLocked || quotaLocked;
    const isCompleted = l.status === "completed";
    const onClick = (e) => {
      if (proLocked) {
        e.preventDefault();
        setUpgradeReason("This is a Pro-only multi-device lab");
        setUpgradeOpen(true);
      } else if (quotaLocked) {
        e.preventDefault();
        setUpgradeReason("You've used your 3 free labs this month. Upgrade to Pro for unlimited access to all 72 labs.");
        setUpgradeOpen(true);
      }
    };
    return (
      <Link
        key={l.id}
        to={`/labs/${l.id}`}
        onClick={onClick}
        className={`bg-[#141416] border p-5 transition-all group flex flex-col ${
          isCompleted
            ? "border-[#00FF41]/40 hover:border-[#00FF41] hover:-translate-y-0.5"
            : locked
            ? "border-zinc-800 hover:border-[#00E5FF]/40 opacity-90"
            : "border-zinc-800 hover:border-[#00E5FF] hover:-translate-y-0.5"
        }`}
        data-testid={`lab-card-${l.id}`}
      >
        <div className="flex items-center justify-between mb-3">
          <DifficultyBadge d={l.difficulty} />
          <div className="flex items-center gap-2">
            {isCompleted && (
              <CheckCircle2 size={16} className="text-[#00FF41]"
                data-testid={`lab-completed-check-${l.id}`} />
            )}
            {l.is_pro_only && (
              <span
                className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-mono font-bold bg-[#00E5FF] text-black flex items-center gap-0.5"
                data-testid={`lab-pro-badge-${l.id}`} title="Pro tier">
                <Lock size={9} strokeWidth={3} /> pro
              </span>
            )}
            {l.is_new && (
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-mono font-bold bg-[#00FF41] text-black"
                data-testid={`lab-new-badge-${l.id}`} title="Recently added">
                new
              </span>
            )}
            {l.cml_topology_id && (
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-mono font-bold border border-[#FF6B35] text-[#FF6B35]"
                data-testid={`lab-cml-badge-${l.id}`} title="Runs on real Cisco IOS via CML (Pro)">
                real IOS
              </span>
            )}
            {l.is_exam && (
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-mono font-bold border border-[#F59E0B] text-[#F59E0B]"
                title="Exam — no hints">
                exam
              </span>
            )}
            <StatusPill s={l.status} score={l.best_score} />
          </div>
        </div>
        <h3 className={`font-display font-bold text-lg transition-colors ${
          isCompleted ? "group-hover:text-[#00FF41]" : "group-hover:text-[#00E5FF]"
        }`}>{l.title}</h3>
        <div className="text-xs text-zinc-500 mt-1 font-mono">{l.topic}</div>
        <p className="text-sm text-zinc-400 mt-3 flex-1 line-clamp-2">{l.description}</p>
        <div className={`mt-3 text-[11px] font-mono opacity-70 group-hover:opacity-100 transition-opacity ${
          isCompleted ? "text-[#00FF41]" : "text-[#00E5FF]"
        }`}>
          {isCompleted ? "review lab →" : proLocked ? "upgrade to unlock →"
            : quotaLocked ? "free limit reached — upgrade →" : "start lab →"}
        </div>
      </Link>
    );
  };

  // Smooth-scroll to the section that matches the URL hash (e.g., #fundamentals).
  // Used by the Dashboard "Domain progress" cards to jump to a domain group
  // without applying any filter.
  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const el = document.querySelector(`[data-domain-anchor="${hash}"]`);
    if (el) {
      // Defer one tick so the section is mounted before we scroll.
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [loading, byDomain]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <main className="max-w-[1800px] mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-1">
              // lab catalog
            </div>
            <h1 className="font-display font-black text-4xl tracking-tighter">
              {(data.labs || []).length} labs · 6 domains
            </h1>
          </div>
          {/* Round 40 — layout toggle. Original (by domain) vs SwitchLab-style
              (by stage). State persists per user in localStorage. */}
          <button
            onClick={toggleLayout}
            data-testid="labs-layout-toggle"
            title={layout === "domain"
              ? "Switch to By Stage view (grouped by CCNA path)"
              : "Switch to By Domain view (original layout)"}
            className="flex items-center gap-2 px-3 py-2 bg-[#141416] border border-zinc-800 hover:border-[#00E5FF] text-xs font-mono uppercase tracking-widest text-zinc-300 hover:text-white transition-colors"
          >
            {layout === "domain" ? <Layers size={13} /> : <LayoutGrid size={13} />}
            {layout === "domain" ? "by stage" : "by domain"}
          </button>
        </div>

        {/* Filters */}
        <div className="bg-[#141416] border border-zinc-800 p-4 mb-6 grid md:grid-cols-[1fr_auto_auto_auto] gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] pl-9 pr-3 py-2 text-sm outline-none font-mono"
              placeholder="search: ospf, vlan, nat…"
              data-testid="labs-search"
            />
          </div>
          <select
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="bg-black border border-zinc-800 px-3 py-2 text-sm font-mono outline-none focus:border-[#00E5FF]"
            data-testid="filter-domain"
          >
            <option value="all">all domains</option>
            {(data.domains || []).map((d) => (
              <option key={d.code} value={d.code}>
                {d.name}
              </option>
            ))}
          </select>
          <select
            value={diff}
            onChange={(e) => setDiff(e.target.value)}
            className="bg-black border border-zinc-800 px-3 py-2 text-sm font-mono outline-none focus:border-[#00E5FF]"
            data-testid="filter-difficulty"
          >
            {DIFFS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-black border border-zinc-800 px-3 py-2 text-sm font-mono outline-none focus:border-[#00E5FF]"
            data-testid="filter-status"
          >
            {STATUSES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono text-sm" data-testid="labs-loading">loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-zinc-500 font-mono text-sm p-6 border border-dashed border-zinc-800" data-testid="labs-empty">
            no labs match your filters. <Filter size={12} className="inline" />
          </div>
        ) : layout === "stage" ? (
          <div className="space-y-10" data-testid="labs-stage-view">
            {stagesFiltered.map((st) => (
              <section
                key={st.slug}
                data-testid={`stage-${st.slug}`}
                className="scroll-mt-24"
              >
                <div className="flex items-baseline gap-3 mb-4 border-b border-zinc-800 pb-2">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono">
                    stage {st.stage < 99 ? st.stage : "—"}
                  </div>
                  <h2 className="font-display font-bold text-xl tracking-tight">{st.title}</h2>
                  <div className="text-xs text-zinc-500 font-mono">
                    {st.sections.reduce((n, s) => n + s.labs.length, 0)} labs
                  </div>
                </div>
                <div className="space-y-6">
                  {st.sections.map((sec) => (
                    <div key={sec.section} data-testid={`stage-section-${sec.section}`}>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-3">
                        {sec.title}
                      </div>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {sec.labs.map(renderLabCard)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(byDomain).map(([dname, group]) => (
              <section
                key={dname}
                data-testid={`section-${dname}`}
                data-domain-anchor={group.code}
                id={group.code}
                className="scroll-mt-24"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-3">
                  {dname}
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {group.labs.map(renderLabCard)}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        reason={upgradeReason}
      />
    </div>
  );
}
